/**
 * S12b CADCORE-GRIPS-SELECT-WIRE — typed-first grip source.
 *
 * The live canvas grip source moves off the `EntityPreview.properties`
 * string bag (`scene/gripCalculation.ts::calculateGrips`, read via
 * `computeGrips.ts`) and onto the canonical typed model
 * (`@dxf-ai-editor/cad-core`'s `getEntityGrips(DrawingEntity)`), exactly as
 * the DEFUSE plan's L4a S12b row prescribes ("grips-источник переезжает на
 * cad-core, как задел под L8"). cad-core reads the `DrawingEntity` fields
 * DIRECTLY (no string round-trip, no display Y-flip — see
 * `cad-core/grips.ts`'s module doc), so:
 *
 *   - Arc / Circle / Ellipse quadrant + endpoint grips land on the RENDERED
 *     WCS curve. The legacy `calculateGrips` baked a screen-space Y-flip
 *     (`cy - r·sin`) meant for the retired SVG stage; the WebGL renderer
 *     draws the arc at `cy + r·sin` (`adapters/sceneGeometryBuilders.ts`),
 *     so the legacy grip sat at the vertically-MIRRORED point off the arc.
 *     Sourcing from cad-core (WCS, no flip) fixes that (visible in the
 *     Cat-C gate; the matching Arc-endpoint drag inverse is corrected in
 *     `state/gripDragTranslate.ts`).
 *   - MULTILEADER grips are the FULL `leaderRoots[]` tree walk (arrow /
 *     intermediate-vertex / landing per leader line, dogleg endpoint incl.
 *     the horizontal fallback, plus the content location) — the
 *     multi-instance hierarchy AutoCAD shows, not the legacy 2-grip
 *     arrow+landing approximation.
 *   - POINT / WIPEOUT / HATCH-boundary / VIEWPORT-4-corner / DIMENSION
 *     third+arc-point grips (absent from `calculateGrips`) now render.
 *
 * The grip `id`s cad-core emits are byte-identical to the legacy ids for
 * every kind whose grip drag is actually committed today
 * (`state/gripDragTranslate.ts`: Line start/midpoint/end, Circle
 * center + quad-N, Arc center/start/end/arc-midpoint, LWPOLYLINE
 * vertex-N + seg-mid-N, Spline cp-N + fit-N, ArrayRect
 * origin/column-count/…, Block insertion, Point location, Ellipse
 * center/major-pos/major-neg/minor-pos/minor-neg, Text/MText insertion —
 * the last three GRIPS-DRAG-MORE-TYPES), so the existing gripId-keyed
 * commit path keeps working unchanged. Kinds whose drag is STILL a
 * documented clean no-op (Dimension / Hatch / Viewport / Revision Cloud /
 * Multileader — the GRIPS-DRAG-MORE-TYPES-2 deferral) render from cad-core
 * and stay no-ops until their typed commit lands.
 *
 * The `Grip.payload` for a typed grip is the cad-core {@link CadGripPoint}
 * itself — carrying the typed `patch` (which `DrawingEntity` field a drag
 * rewrites), consumed by the typed hot-grip selection model
 * (`hotGripStore.ts`) and, later, L8's typed grip-commit.
 *
 * Dynamic-block / MINSERT parameter grips (visibility / lookup / flip /
 * per-axis stretch) are a separate ~1100-line string-bag subsystem
 * (`scene/blockGripCollector.ts`) explicitly OUT of cad-core's Block grip
 * scope (`cad-core/grips.ts` module doc); they are layered on top from the
 * SAME one truth both paths share (`blockDynamicGrips` in `computeGrips.ts`)
 * by `buildGripSet`, never re-derived here.
 */

import type {
  DrawingEntity,
  MultileaderEntity,
} from "@dxf-ai-editor/drawing-model";
import {
  getEntityGrips,
  type GripPoint as CadGripPoint,
} from "@dxf-ai-editor/cad-core";

import type { Grip, GripKind, GripShape } from "./computeGrips";

/**
 * Map a cad-core `GripPoint` (its semantic `id` + `kind`) to the scene3d
 * overlay's `GripKind` + glyph `GripShape`. Exhaustive over cad-core's
 * `GripKind` union (compile-time `never` guard) so a new cad-core grip kind
 * cannot silently fall through to a wrong glyph.
 *
 * `entityType` selects the INSERT alignment glyph: AutoCAD paints a Block
 * insertion grip as the alignment parallelogram, but a Text / MText / Point
 * insertion grip as an ordinary square (mirrors `computeGrips.ts`'s
 * `entityGripKind` `insertion` branch).
 */
export function mapCadGripKind(
  grip: CadGripPoint,
  entityType: DrawingEntity["type"],
): { kind: GripKind; shape: GripShape } {
  switch (grip.kind) {
    case "endpoint":
      return { kind: "endpoint", shape: "square" };
    case "midpoint":
      return { kind: "midpoint", shape: "square" };
    case "center":
      return { kind: "center", shape: "square" };
    case "quadrant":
    case "major-axis":
    case "minor-axis":
      // Circle / Arc / Ellipse resize grips — square glyph, drag keys off the
      // `quad-*` / `major-*` / `minor-*` id (radiusMode is on the cad-core
      // payload for the future typed commit).
      return { kind: "quadrant", shape: "square" };
    case "insertion":
      return {
        kind: "insertion",
        shape: entityType === "Block" ? "alignment" : "square",
      };
    case "corner":
      return { kind: "corner", shape: "square" };
    case "vertex":
      return { kind: "polyline-vertex", shape: "square" };
    case "segment-mid":
      return { kind: "polyline-segment-mid", shape: "square" };
    case "control-point":
      return { kind: "spline-control-point", shape: "square" };
    case "fit-point":
      return { kind: "spline-fit-point", shape: "square" };
    case "ext-line":
      return { kind: "extension-line", shape: "square" };
    case "dim-line":
    case "third-point":
    case "arc-point":
      // Every DIMENSION definition-point grip paints as a square dim grip;
      // the distinct `id` (dimline / third-point / arc-point) keeps them
      // individually addressable for the deferred typed dimension commit.
      return { kind: "dim-line", shape: "square" };
    case "text-location":
      return { kind: "mleader-text", shape: "square" };
    case "mleader-arrow":
      return { kind: "mleader-arrow", shape: "square" };
    case "mleader-vertex":
      return { kind: "mleader-vertex", shape: "square" };
    case "mleader-landing":
      return { kind: "mleader-landing", shape: "square" };
    case "mleader-dogleg":
      return { kind: "mleader-dogleg", shape: "square" };
    case "array-origin":
      return { kind: "array-origin", shape: "square" };
    case "array-column-count":
      return { kind: "array-column-count", shape: "triangle" };
    case "array-row-count":
      return { kind: "array-row-count", shape: "triangle" };
    case "array-column-spacing":
      return { kind: "array-column-spacing", shape: "diamond" };
    case "array-row-spacing":
      return { kind: "array-row-spacing", shape: "diamond" };
    default: {
      const _never: never = grip.kind;
      void _never;
      return { kind: "endpoint", shape: "square" };
    }
  }
}

/**
 * The flat leader-hierarchy address the MLEADER grip context menu
 * (`AppGripMenus.tsx`) reads off `gripPayload` — `rootIndex` / `nodeIndex` /
 * `lineIndex` / `vertexIndex` — to dispatch add/remove-leader + add/remove-
 * vertex on the RIGHT leader line + vertex.
 */
interface MleaderGripPath {
  readonly rootIndex: number;
  readonly nodeIndex: number;
  readonly lineIndex?: number;
  readonly vertexIndex?: number;
}

/**
 * Reconstruct the leader-hierarchy address (`MleaderGripPath`) a MULTILEADER
 * grip addresses, from the cad-core `GripPoint.id`. The legacy
 * `computeMultileaderGrips` payload carried `rootIndex` / `nodeIndex` /
 * `lineIndex` / `vertexIndex` explicitly, and the MLEADER context menu
 * (`AppGripMenus.tsx`) still reads them off `gripPayload` (defaulting a
 * missing value to `0`); the cad-core `GripPoint` encodes the SAME address
 * ONLY in its `id`, so the typed-first payload must re-attach it or right-
 * clicking a grip on any non-first leader line / intermediate vertex would
 * add/remove leader 0 / vertex 0 instead of the clicked one (Codex P1).
 *
 * The four id shapes mirror `cad-core/grips.ts::multileaderGrips` (byte-
 * identical to `computeGrips.ts::computeMultileaderGrips`):
 *   - `mleader-arrow-{ri}-{ni}-{li}`     → the leader line's FIRST vertex (0)
 *   - `mleader-vertex-{ri}-{ni}-{li}-{vi}` → that intermediate vertex
 *   - `mleader-landing-{ri}-{ni}-{li}`   → the leader line's LAST vertex
 *     (matches the legacy landing payload's `vertexIndex: lastIdx`; the count
 *     is read from the typed `entity` since the id does not encode it)
 *   - `mleader-dogleg-{ri}-{ni}`         → root/node only (no line/vertex —
 *     the legacy dogleg payload carried only `rootIndex`/`nodeIndex` too)
 * The `text` grip carries no hierarchy address (legacy `{ kind: "text" }`).
 */
function parseMleaderGripPath(
  gripId: string,
  entity: MultileaderEntity,
): MleaderGripPath | null {
  let m = /^mleader-vertex-(\d+)-(\d+)-(\d+)-(\d+)$/.exec(gripId);
  if (m) {
    return {
      rootIndex: Number(m[1]),
      nodeIndex: Number(m[2]),
      lineIndex: Number(m[3]),
      vertexIndex: Number(m[4]),
    };
  }
  m = /^mleader-arrow-(\d+)-(\d+)-(\d+)$/.exec(gripId);
  if (m) {
    return {
      rootIndex: Number(m[1]),
      nodeIndex: Number(m[2]),
      lineIndex: Number(m[3]),
      vertexIndex: 0,
    };
  }
  m = /^mleader-landing-(\d+)-(\d+)-(\d+)$/.exec(gripId);
  if (m) {
    const ri = Number(m[1]);
    const ni = Number(m[2]);
    const li = Number(m[3]);
    const verts = entity.leaderRoots[ri]?.nodes[ni]?.lines[li]?.vertices;
    return {
      rootIndex: ri,
      nodeIndex: ni,
      lineIndex: li,
      vertexIndex: verts && verts.length > 0 ? verts.length - 1 : 0,
    };
  }
  m = /^mleader-dogleg-(\d+)-(\d+)$/.exec(gripId);
  if (m) {
    return { rootIndex: Number(m[1]), nodeIndex: Number(m[2]) };
  }
  return null;
}

/**
 * Build the `Grip.payload` for one typed grip. Normally the cad-core
 * `GripPoint` itself (carrying its typed `patch`); for a MULTILEADER grip the
 * leader-hierarchy address (`parseMleaderGripPath`) is spread on TOP so the
 * MLEADER context menu keeps operating on the exact leader line + vertex the
 * user clicked (Codex P1). The spread preserves `patch` (the text grip's) and
 * the cad-core `id`/`kind`, so `gripToHotRef` still lifts `patch` unchanged.
 */
function gripPayloadFor(gp: CadGripPoint, entity: DrawingEntity): unknown {
  if (entity.type !== "Multileader") return gp;
  const path = parseMleaderGripPath(gp.id, entity);
  return path ? { ...gp, ...path } : gp;
}

/**
 * Typed-first BASE grip set for one entity, computed from the canonical
 * `DrawingEntity` via cad-core. This is the entity-SHAPE grip set only; the
 * caller (`buildGripSet`) layers the dynamic-block / MINSERT grips
 * (`blockDynamicGrips`) and the ARRAYRECT insertion chevrons
 * (`computeArrayInsertionGrips`) on top — both out of cad-core's scope.
 *
 * The returned `Grip.payload` is the cad-core `GripPoint` (carrying the
 * typed `patch`), and `rotationDeg` is threaded through for the directional
 * ARRAYRECT count grips (glyph apex along the rotated axis — AutoCAD parity).
 */
export function typedEntityGrips(
  entity: DrawingEntity,
  entityId: string,
): Grip[] {
  const out: Grip[] = [];
  for (const gp of getEntityGrips(entity)) {
    const { kind, shape } = mapCadGripKind(gp, entity.type);
    const grip: Grip = {
      id: gp.id,
      entityId,
      kind,
      shape,
      // DOCUMENT-frame (raw WCS) — cad-core reads `DrawingEntity` fields directly,
      // matching the legacy string-bag grips, the typed hot-grip model, and the
      // grip-drag COMMIT (which maps the pointer back via `unapplyOffset`). The
      // OFFSET-WORLD conversion for the render camera (`DXF − coordinateOrigin`)
      // is applied ONCE at the projection choke-point (`gripsOverlay.tsx`'s
      // `projected` map via `applyOffset`) so a non-zero-origin import lands its
      // grips on the geometry; do NOT re-offset here (Codex S12b P2).
      world: { x: gp.x, y: gp.y },
      payload: gripPayloadFor(gp, entity),
      ...(gp.rotationDeg !== undefined ? { rotationDeg: gp.rotationDeg } : {}),
    };
    out.push(grip);
  }
  return out;
}
